import { CopyableText } from "@via/components/copyable-text/copyable-text";
import Modal from "@via/components/modal";
import { errorMessage } from "@via/pages/projects/metadata/configuration/data-import/data-import.api.tsx";
import {
  credentialErrorMessage,
  KEY_TONE_CLASS,
  keyExpiryTone,
} from "@via/pages/projects/metadata/configuration/data-import/utils/form";
import { formatDateString } from "@via/utils/helpers";
import type { VmetaImportLoginKeyResult } from "common";
import { FC, useState } from "react";

/** What generating a key returns. */
export type GeneratedKey = VmetaImportLoginKeyResult;

/** The key pair of one credential. Every import using it needs the new certificate. */
const KeyPanel: FC<{
  loginId: number | null;
  certificate: string | null;
  keyExpiresAt: string | null;
  version: number;
  usedBy: number;
  generate: (id: number, version: number) => Promise<GeneratedKey>;
  onGenerated: (result: GeneratedKey) => void;
}> = ({
  loginId,
  certificate,
  keyExpiresAt,
  version,
  usedBy,
  generate,
  onGenerated,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const tone = keyExpiryTone(keyExpiresAt);
  const when = keyExpiresAt
    ? formatDateString(new Date(keyExpiresAt), "LLL")
    : "";

  const onConfirm = async () => {
    if (loginId === null) return;
    setError(null);
    setIsPending(true);
    try {
      onGenerated(await generate(loginId, version));
    } catch (err) {
      setError(
        credentialErrorMessage(
          err,
          "The key could not be generated.",
          errorMessage
        )
      );
    } finally {
      setIsPending(false);
    }
  };

  return (
    <section
      aria-labelledby="import-key-heading"
      className="space-y-3 rounded-md border border-gray-200 p-4">
      <h4
        id="import-key-heading"
        className="text-sm font-semibold text-gray-900">
        Key pair
      </h4>
      {certificate ? (
        <>
          <textarea
            aria-label="Public certificate"
            readOnly
            rows={6}
            value={certificate}
            className="block w-full rounded-md border-0 font-mono text-xs ring-1 ring-inset ring-gray-300"
          />
          <CopyableText
            value={certificate}
            showValue={false}
            buttonLabel="Copy certificate"
          />
        </>
      ) : (
        <p className="text-sm text-gray-500">
          No key yet. Generate one, then register its certificate in
          MyDataHelps.
        </p>
      )}
      {keyExpiresAt && (
        <p className={`text-sm ${KEY_TONE_CLASS[tone]}`}>
          {tone === "expired"
            ? `Expired on ${when}. Generate a new key and register it in MyDataHelps.`
            : tone === "soon"
              ? `Expires on ${when}. Generate a new key soon and register it in MyDataHelps.`
              : `Expires on ${when}.`}
        </p>
      )}
      <button
        type="button"
        disabled={loginId === null || isPending}
        onClick={() => setConfirmOpen(true)}
        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
        Generate new key
      </button>
      {loginId === null && (
        <p className="text-xs text-gray-500">Save the credential first.</p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      <Modal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title="Generate a new key?"
        confirmText="Generate key now"
        onConfirm={onConfirm}>
        <p className="text-sm">
          The current key stops working immediately.{" "}
          {usedBy > 0
            ? usedBy === 1
              ? "The import using this credential stops working until the new certificate is registered in MyDataHelps."
              : `All ${usedBy} imports using this credential stop working until the new certificate is registered in MyDataHelps.`
            : "Register the new certificate in MyDataHelps before the next import runs."}
        </p>
      </Modal>
    </section>
  );
};

export default KeyPanel;
